package kz.dentalcrm.app.screens.appointments.add

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.at
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_appointment_created
import dentalcrm.composeapp.generated.resources.month_eight_short
import dentalcrm.composeapp.generated.resources.month_eleven_short
import dentalcrm.composeapp.generated.resources.month_five_short
import dentalcrm.composeapp.generated.resources.month_four_short
import dentalcrm.composeapp.generated.resources.month_nine_short
import dentalcrm.composeapp.generated.resources.month_one_short
import dentalcrm.composeapp.generated.resources.month_seven_short
import dentalcrm.composeapp.generated.resources.month_six_short
import dentalcrm.composeapp.generated.resources.month_ten_short
import dentalcrm.composeapp.generated.resources.month_three_short
import dentalcrm.composeapp.generated.resources.month_twelve_short
import dentalcrm.composeapp.generated.resources.month_two_short
import dentalcrm.composeapp.generated.resources.week_day_five_short
import dentalcrm.composeapp.generated.resources.week_day_four_short
import dentalcrm.composeapp.generated.resources.week_day_one_short
import dentalcrm.composeapp.generated.resources.week_day_seven_short
import dentalcrm.composeapp.generated.resources.week_day_six_short
import dentalcrm.composeapp.generated.resources.week_day_three_short
import dentalcrm.composeapp.generated.resources.week_day_two_short
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.Selectable
import kz.dentalcrm.app.core.ui.InputValue
import kz.dentalcrm.app.core.utils.pad
import kz.dentalcrm.app.core.utils.plusMinutes
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.core.utils.toSqlString
import kz.dentalcrm.app.data.network.request_model.AppointmentRequest
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.domain.AppointmentRepository
import kz.dentalcrm.app.screens.appointments.add.mvi.AddAppointmentViewEvent
import kz.dentalcrm.app.screens.appointments.add.mvi.AddAppointmentViewSideEffect
import kz.dentalcrm.app.screens.appointments.add.mvi.AddAppointmentViewState
import kz.dentalcrm.app.screens.appointments.add.mvi.AddAppointmentViewState.Step
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

class AddAppointmentViewModel : BaseStateViewModel<AddAppointmentViewEvent,
        AddAppointmentViewState,
        AddAppointmentViewSideEffect>() {

    private val appointmentRepository: AppointmentRepository by inject()


    private var searchPatientJob: Job? = null

    override fun setInitialState(): AddAppointmentViewState = AddAppointmentViewState()

    override fun handleEvents(event: AddAppointmentViewEvent) {
        when (event) {
            is AddAppointmentViewEvent.SelectDateTime -> {
                launch {
                    val months = listOf(
                        getString(Res.string.month_one_short),
                        getString(Res.string.month_two_short),
                        getString(Res.string.month_three_short),
                        getString(Res.string.month_four_short),
                        getString(Res.string.month_five_short),
                        getString(Res.string.month_six_short),
                        getString(Res.string.month_seven_short),
                        getString(Res.string.month_eight_short),
                        getString(Res.string.month_nine_short),
                        getString(Res.string.month_ten_short),
                        getString(Res.string.month_eleven_short),
                        getString(Res.string.month_twelve_short),
                    )
                    val at = getString(Res.string.at)
                    val dateTime = event.dateTime
                    val weekDay = dateTime.dayOfWeek.ordinal.getWeekDay()
                    setState {
                        copy(
                            selectDateTime = dateTime,
                            dateFormatted = "${dateTime.day} ${months[dateTime.month.ordinal]} ${dateTime.year} $at ${dateTime.hour}:${dateTime.minute.pad()} ($weekDay)"
                        )
                    }
                    checkButtonState()
                }
            }

            is AddAppointmentViewEvent.RemovePatient -> {
                setState {
                    copy(selectedPatient = null)
                }
                checkButtonState()
            }

            is AddAppointmentViewEvent.TitleChanged -> {
                setState {
                    copy(
                        titleInput = InputValue(
                            value = event.value
                        )
                    )
                }
            }

            is AddAppointmentViewEvent.SearchPatient -> {
                setState {
                    copy(
                        searchPatientInput = InputValue(value = event.search),
                        suggestedPatients = persistentListOf()
                    )
                }
                searchPatients(event.search)
            }

            is AddAppointmentViewEvent.SelectPatient -> {
                selectPatient(patient = event.patient)
                checkButtonState()
            }

            is AddAppointmentViewEvent.SelectEmployee -> {
                val employees = viewState.value.employees.map {
                    it.copy(selected = it.data.id == event.employee.id)
                }
                setState {
                    copy(employees = employees.toPersistentList())
                }
                checkButtonState()
            }

            AddAppointmentViewEvent.CreateAppointment -> {
                createAppointment()
            }
        }
    }

    private fun selectPatient(patient: PatientData) {
        setState { copy(selectedPatient = patient) }
    }

    private fun searchPatients(query: String) {
        searchPatientJob?.cancel()
        if (query.isNotEmpty()) {
            searchPatientJob = launch {
                delay(1000L)
                appointmentRepository.searchPatients(query)
                    .stateHandle { data ->
                        setState {
                            copy(suggestedPatients = data.data.take(5).toPersistentList())
                        }
                    }
            }
        } else {
            setState {
                copy(suggestedPatients = persistentListOf())
            }
        }
    }

    private fun loadEmployees() {
        launch {
            appointmentRepository.getEmployees(page = 0, pageSize = 100).stateHandle { data ->
                setState {
                    copy(
                        employees = data?.data
                            ?.map { Selectable(data = it, selected = false) }
                            ?.toPersistentList() ?: persistentListOf()
                    )
                }
            }
        }
    }
    
    private fun createAppointment() {
        launch {
            val currentState = viewState.value
            val startDateTime = currentState.selectDateTime
            val endDateTime = startDateTime?.plusMinutes(20)
            val patient = viewState.value.selectedPatient?.copy(
                patronymic = if (viewState.value.selectedPatient?.patronymic.isNullOrEmpty()) "  "
                    else viewState.value.selectedPatient?.patronymic
            )
            val title = currentState.titleInput.value
            setState {
                copy(appointmentState = AddAppointmentViewState.AddAppointmentState.Loading)
            }
            appointmentRepository.createAppointment(
                appointmentRequest = AppointmentRequest(
                    cash_box_id = null,
                    employee = currentState.employees.firstOrNull { it.selected }?.data,
                    ends_at = endDateTime?.toSqlString(),
                    is_first_visit = false,
                    patient = patient,
                    price = 0,
                    services = emptyList(),
                    status = null,
                    comment = currentState.titleInput.value.takeIf { it.isNotEmpty() },
                    starts_at = startDateTime?.toSqlString(),
                    title = title.ifEmpty { null },
                    treatment_course = null
                )
            ).stateHandle(
                onError = { _, _ ->
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(appointmentState = AddAppointmentViewState.AddAppointmentState.Idle)
                    }
                },
                onFailure = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(appointmentState = AddAppointmentViewState.AddAppointmentState.Idle)
                    }
                },
                onSuccess = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_appointment_created), SnackbarEvent.SnackbarType.Green))
                    setState {
                        copy(appointmentState = AddAppointmentViewState.AddAppointmentState.Idle)
                    }
                    setEffect { AddAppointmentViewSideEffect.NavigateBack }
                }
            )
        }
    }

    private fun checkButtonState() {
        val currentState = viewState.value
        val patientSelected = currentState.selectedPatient != null
        val dateSelected = currentState.selectDateTime != null
        val employeeSelected = currentState.employees.any { it.selected }
        val step = when {
            patientSelected && dateSelected && employeeSelected -> Step.Fourth
            patientSelected && dateSelected -> Step.Third
            patientSelected -> Step.Second
            else -> Step.First
        }
        setState {
            copy(
                step = step,
                enableCreateAppointment = step is Step.Fourth
            )
        }
    }

    init {
        loadEmployees()
    }
}

suspend fun Int.getWeekDay(): String {
    return when (this@getWeekDay) {
        0 -> getString(Res.string.week_day_one_short)
        1 -> getString(Res.string.week_day_two_short)
        2 -> getString(Res.string.week_day_three_short)
        3 -> getString(Res.string.week_day_four_short)
        4 -> getString(Res.string.week_day_five_short)
        5 -> getString(Res.string.week_day_six_short)
        6 -> getString(Res.string.week_day_seven_short)
        else -> ""
    }
}